import React from 'react';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  MinusCircleOutlined,
  QuestionCircleOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { JobStepState, JobStep, type JobStateResult } from 'types/types';
import './JobStatesWorkflow.scss';

// Extracted configuration object using Enums instead of magic switch integers
const WORKFLOW_STATE_CONFIG: Record<
  number,
  { node: React.ReactNode; modifier: string }
> = {
  2: { // SUCCESS
    node: <CheckCircleOutlined />,
    modifier: 'job-states-workflow__step-icon--success',
  },
  [-1]: { // FAILURE
    node: <CloseCircleOutlined />,
    modifier: 'job-states-workflow__step-icon--failure',
  },
  1: { // RUNNING
    node: <SyncOutlined spin />,
    modifier: 'job-states-workflow__step-icon--running',
  },
  0: { // NOT_RUNNING
    node: <ClockCircleOutlined />,
    modifier: 'job-states-workflow__step-icon--pending',
  },
  3: { // PARTIAL_SUCCESS
    node: <ExclamationCircleOutlined />,
    modifier: 'job-states-workflow__step-icon--partial',
  },
  4: { // SKIPPED
    node: <MinusCircleOutlined />,
    modifier: 'job-states-workflow__step-icon--skipped',
  },
};

const DEFAULT_STATE_CONFIG = {
  node: <QuestionCircleOutlined />,
  modifier: 'job-states-workflow__step-icon--unknown',
};

export type JobStatesWorkflowProps = {
  jobStates: JobStateResult[];
};

export function JobStatesWorkflow({ jobStates }: JobStatesWorkflowProps) {
  if (!jobStates?.length) return null;

  return (
    <div className="job-states-workflow">
      {jobStates.map((state, index) => {
        const stateCode = Number(state.stateResult);
        const config = WORKFLOW_STATE_CONFIG[stateCode] ?? DEFAULT_STATE_CONFIG;
        
        const stepLabel = JobStep[state.stateId] ?? `Step ${state.stateId}`;
        const resultLabel = JobStepState[stateCode] ?? String(state.stateResult);

        return (
          <React.Fragment key={`${state.stateId}-${index}`}>
            {index > 0 && (
              <div className="job-states-workflow__connector" aria-hidden="true" />
            )}
            <div className="job-states-workflow__step" title={`${stepLabel}: ${resultLabel}`}>
              <span className={`job-states-workflow__step-icon ${config.modifier}`}>
                {config.node}
              </span>
              <span className="job-states-workflow__step-name">
                {stepLabel}
              </span>
              <span className="job-states-workflow__step-result">
                {resultLabel}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}